import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { RefreshCw, Trash2 } from "lucide-react";
import { PAYMENT_OPTIONS } from "./SubscriptionCard";

const PaymentSelect = ({ value, onChange, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-10" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>
      {PAYMENT_OPTIONS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

const CATEGORIES = [
  { value: "STANDARD", label: "Standard" }, { value: "ETUDIANT", label: "Étudiant" },
  { value: "SENIOR", label: "Senior" }, { value: "FAMILIAL", label: "Familial" },
  { value: "PRIO_PACK", label: "Prio Pack" }, { value: "SINGLE_ENTRY", label: "Entrée unique" },
];

const CategorySelect = ({ value, onChange, testId }) => (
  <Select value={value || "STANDARD"} onValueChange={onChange}>
    <SelectTrigger className="h-10" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
  </Select>
);

const FormField = ({ label, children }) => (
  <div><label className="text-xs font-medium text-[#32536a] mb-1 block">{label}</label>{children}</div>
);

// ─── Add Subscription ────────────────────────────────────────────────────────
export const AddSubscriptionDialog = ({ open, onOpenChange, catalog, form, setForm, onSelectPlan, onSubmit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader><DialogTitle>Nouvel abonnement</DialogTitle></DialogHeader>
      <div className="flex flex-col gap-3 py-2">
        <Select onValueChange={onSelectPlan}>
          <SelectTrigger className="h-10"><SelectValue placeholder="Choisir un plan du catalogue" /></SelectTrigger>
          <SelectContent>{catalog.map(c => <SelectItem key={c.id} value={c.id}>{c.category} - {c.name} ({c.price}€)</SelectItem>)}</SelectContent>
        </Select>
        <Input placeholder="Nom du plan" value={form.plan_name} onChange={e => setForm({...form, plan_name: e.target.value})} />
        <Input type="number" placeholder="Prix €" value={form.price_eur} onChange={e => setForm({...form, price_eur: e.target.value})} />
        <PaymentSelect value={form.payment_type} onChange={v => setForm({...form, payment_type: v})} testId="add-sub-payment" />
        <Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} />
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
        <Button onClick={onSubmit} className="bg-[#1a2744] hover:bg-[#243459] text-[#e8d44d]">Créer</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// ─── Renewal ─────────────────────────────────────────────────────────────────
export const RenewalDialog = ({ open, onOpenChange, catalog, form, setForm, onSelectPlan, onSubmit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-[480px]">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><RefreshCw size={18} className="text-green-600" /> Renouvellement d'abonnement</DialogTitle>
      </DialogHeader>
      <div className="flex flex-col gap-3 py-2">
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
          Un nouvel abonnement sera créé à la suite de l'actuel. Ajustez les informations si nécessaire.
        </div>
        <FormField label="Plan depuis le catalogue">
          <Select onValueChange={onSelectPlan}>
            <SelectTrigger className="h-10"><SelectValue placeholder="Choisir un plan (optionnel)" /></SelectTrigger>
            <SelectContent>{catalog.map(c => <SelectItem key={c.id} value={c.id}>{c.category} - {c.name} ({c.price}€)</SelectItem>)}</SelectContent>
          </Select>
        </FormField>
        <FormField label="Nom du plan"><Input data-testid="renewal-plan-name" value={form.plan_name || ""} onChange={e => setForm({...form, plan_name: e.target.value})} /></FormField>
        <FormField label="Catégorie"><CategorySelect value={form.plan_category} onChange={v => setForm({...form, plan_category: v})} testId="renewal-category" /></FormField>
        <FormField label="Prix (€)"><Input data-testid="renewal-price" type="number" step="0.01" value={form.price_eur || 0} onChange={e => setForm({...form, price_eur: e.target.value})} /></FormField>
        <FormField label="Mode de paiement"><PaymentSelect value={form.payment_type || "CARD"} onChange={v => setForm({...form, payment_type: v})} testId="renewal-payment" /></FormField>
        <FormField label="Date de début du nouvel abonnement"><Input data-testid="renewal-start-date" type="date" value={form.start_date || ""} onChange={e => setForm({...form, start_date: e.target.value})} /></FormField>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
        <Button data-testid="submit-renewal-btn" onClick={onSubmit} className="bg-green-600 hover:bg-green-700 text-white"><RefreshCw size={14} className="mr-1" /> Valider le renouvellement</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// ─── Edit Member ─────────────────────────────────────────────────────────────
export const EditMemberDialog = ({ open, onOpenChange, form, setForm, onSave }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader><DialogTitle>Modifier le membre</DialogTitle></DialogHeader>
      <div className="flex flex-col gap-3 py-2">
        <Input placeholder="Nom" value={form.last_name || ""} onChange={e => setForm({...form, last_name: e.target.value})} />
        <Input placeholder="Prénom" value={form.first_name || ""} onChange={e => setForm({...form, first_name: e.target.value})} />
        <Input placeholder="Email" value={form.email || ""} onChange={e => setForm({...form, email: e.target.value})} />
        <Input placeholder="Téléphone" value={form.phone || ""} onChange={e => setForm({...form, phone: e.target.value})} />
        <textarea placeholder="Notes" value={form.notes || ""} onChange={e => setForm({...form, notes: e.target.value})} className="border rounded-md p-2 text-sm min-h-[60px]" />
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
        <Button onClick={onSave} className="bg-[#1a2744] hover:bg-[#243459] text-[#e8d44d]">Enregistrer</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// ─── Suspension ──────────────────────────────────────────────────────────────
export const SuspensionDialog = ({ open, onOpenChange, form, setForm, onSubmit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader><DialogTitle>Ajouter une suspension</DialogTitle></DialogHeader>
      <div className="flex flex-col gap-3 py-2">
        <FormField label="Date début"><Input type="date" value={form.suspension_start} onChange={e => setForm({...form, suspension_start: e.target.value})} /></FormField>
        <FormField label="Date fin"><Input type="date" value={form.suspension_end} onChange={e => setForm({...form, suspension_end: e.target.value})} /></FormField>
        <Select value={form.reason} onValueChange={v => setForm({...form, reason: v})}>
          <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="MEDICAL">Médical</SelectItem><SelectItem value="OTHER">Autre</SelectItem></SelectContent>
        </Select>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
        <Button onClick={onSubmit} className="bg-[#1a2744] hover:bg-[#243459] text-[#e8d44d]" data-testid="add-suspension-btn">Ajouter</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// ─── Edit Subscription ───────────────────────────────────────────────────────
export const EditSubscriptionDialog = ({ open, onOpenChange, form, setForm, onSave }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader><DialogTitle>Modifier l'abonnement</DialogTitle></DialogHeader>
      <div className="flex flex-col gap-3 py-2">
        <FormField label="Nom du plan"><Input data-testid="edit-sub-plan-name" value={form.plan_name || ""} onChange={e => setForm({...form, plan_name: e.target.value})} /></FormField>
        <FormField label="Catégorie"><CategorySelect value={form.plan_category} onChange={v => setForm({...form, plan_category: v})} testId="edit-sub-category" /></FormField>
        <FormField label="Prix (€)"><Input data-testid="edit-sub-price" type="number" step="0.01" value={form.price_eur || 0} onChange={e => setForm({...form, price_eur: e.target.value})} /></FormField>
        <FormField label="Mode de paiement"><PaymentSelect value={form.payment_type || "CARD"} onChange={v => setForm({...form, payment_type: v})} testId="edit-sub-payment" /></FormField>
        <FormField label="Date de début"><Input data-testid="edit-sub-start-date" type="date" value={form.start_date || ""} onChange={e => setForm({...form, start_date: e.target.value})} /></FormField>
        <FormField label="Date de fin (correction manuelle)"><Input data-testid="edit-sub-end-date" type="date" value={form.end_date || ""} onChange={e => setForm({...form, end_date: e.target.value})} /></FormField>
        <FormField label="Statut de l'abonnement">
          <Select value={form.subscription_status || "AUTO"} onValueChange={v => setForm({...form, subscription_status: v})}>
            <SelectTrigger className="h-10" data-testid="edit-sub-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="AUTO">Automatique (selon dates)</SelectItem>
              <SelectItem value="ACTIVE">Actif (forcé)</SelectItem>
              <SelectItem value="INACTIVE">Inactif (forcé)</SelectItem>
            </SelectContent>
          </Select>
        </FormField>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
        <Button data-testid="save-edit-sub-btn" onClick={onSave} className="bg-[#1a2744] hover:bg-[#243459] text-[#e8d44d]">Enregistrer</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// ─── Delete Member Confirm ───────────────────────────────────────────────────
export const DeleteMemberDialog = ({ open, onOpenChange, member, onConfirm }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader><DialogTitle>Confirmer la suppression</DialogTitle></DialogHeader>
      <div className="py-4">
        <p className="text-[#577083]">Êtes-vous sûr de vouloir supprimer le membre <strong>{member?.first_name} {member?.last_name}</strong> ?</p>
        <p className="text-sm text-red-500 mt-2">Cette action est irréversible. Tous les abonnements et suspensions associés seront également supprimés.</p>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
        <Button onClick={onConfirm} className="bg-red-500 hover:bg-red-600 text-white"><Trash2 size={14} className="mr-1" /> Supprimer définitivement</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
